import { useEffect, useMemo, useRef, useState } from 'react';

// ────────────────────────────────────────────────────────────────────────────
// Utilitaires de dates (aucune dépendance externe)
// ────────────────────────────────────────────────────────────────────────────
const toISO = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d - tzOffset).toISOString().slice(0, 10);
};

const fromISO = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
};

const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};

const startOfWeek = (date) => {
    const d = new Date(date);
    const day = (d.getDay() + 6) % 7; // Lundi = 0
    return addDays(d, -day);
};

const endOfWeek = (date) => addDays(startOfWeek(date), 6);

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

const listDatesBetween = (fromIso, toIso) => {
    const dates = [];
    let cursor = fromISO(fromIso);
    const end = fromISO(toIso);
    while (toISO(cursor) <= toISO(end)) {
        dates.push(toISO(cursor));
        cursor = addDays(cursor, 1);
        if (dates.length > 366) break; // garde-fou
    }
    return dates;
};

const formatDisplay = (iso) => {
    if (!iso) return '';
    return fromISO(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Formatage monétaire cohérent partout dans le composant
const formatAr = (value) => `Ar ${Number(value || 0).toLocaleString('fr-FR')}`;

const today = new Date();

const PRESETS = [
    { key: 'today', label: "Aujourd'hui", range: () => [today, today] },
    { key: 'yesterday', label: 'Hier', range: () => [addDays(today, -1), addDays(today, -1)] },
    { key: 'this_week', label: 'Cette semaine', range: () => [startOfWeek(today), today] },
    { key: 'last_week', label: 'La semaine dernière', range: () => [startOfWeek(addDays(today, -7)), endOfWeek(addDays(today, -7))] },
    { key: 'this_month', label: 'Ce mois', range: () => [startOfMonth(today), today] },
    { key: 'last_month', label: 'Le mois dernier', range: () => [startOfMonth(addDays(startOfMonth(today), -1)), endOfMonth(addDays(startOfMonth(today), -1))] },
    { key: 'last_7', label: 'Il y a 7 jours', range: () => [addDays(today, -6), today] },
    { key: 'last_30', label: 'Il y a 30 jours', range: () => [addDays(today, -29), today] },
];

const MONTH_LABELS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// ────────────────────────────────────────────────────────────────────────────
// Sélecteur de plage de dates — reproduit l'interface Loyverse :
// calendrier à gauche, raccourcis à droite, dates personnalisées en bas,
// boutons Annuler / Terminé.
// ────────────────────────────────────────────────────────────────────────────
function DateRangePicker({ dateFrom, dateTo, onChange }) {
    const [open, setOpen] = useState(false);
    const [pendingFrom, setPendingFrom] = useState(dateFrom);
    const [pendingTo, setPendingTo] = useState(dateTo);
    const [visibleMonth, setVisibleMonth] = useState(() => fromISO(dateTo || toISO(today)));
    const containerRef = useRef(null);

    useEffect(() => {
        if (open) {
            setPendingFrom(dateFrom);
            setPendingTo(dateTo);
            setVisibleMonth(fromISO(dateTo || toISO(today)));
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const onClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const applyPreset = (preset) => {
        const [from, to] = preset.range();
        setPendingFrom(toISO(from));
        setPendingTo(toISO(to));
        setVisibleMonth(to);
    };

    const handleDayClick = (dayIso) => {
        if (!pendingFrom || (pendingFrom && pendingTo)) {
            setPendingFrom(dayIso);
            setPendingTo(null);
            return;
        }
        if (dayIso < pendingFrom) {
            setPendingTo(pendingFrom);
            setPendingFrom(dayIso);
        } else {
            setPendingTo(dayIso);
        }
    };

    const confirm = () => {
        const from = pendingFrom || toISO(today);
        const to = pendingTo || from;
        onChange(from > to ? to : from, from > to ? from : to);
        setOpen(false);
    };

    const cancel = () => setOpen(false);

    const monthDays = useMemo(() => {
        const first = startOfMonth(visibleMonth);
        const startOffset = (first.getDay() + 6) % 7; // Lundi = 0
        const gridStart = addDays(first, -startOffset);
        return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
    }, [visibleMonth]);

    const inRange = (dayIso) => pendingFrom && pendingTo && dayIso >= pendingFrom && dayIso <= pendingTo;
    const isEdge = (dayIso) => dayIso === pendingFrom || dayIso === pendingTo;

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
                <svg className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="font-medium">{formatDisplay(dateFrom)} – {formatDisplay(dateTo)}</span>
                <svg className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
                </svg>
            </button>

            {open && (
                <div className="absolute left-1/2 -translate-x-1/2 z-50 mt-2 w-[560px] max-w-[92vw] rounded-xl border border-gray-200 bg-white p-4 shadow-2xl">
                    <div className="flex gap-4">
                        {/* Calendrier */}
                        <div className="flex-1">
                            <div className="mb-2 flex items-center justify-between">
                                <button type="button" onClick={() => setVisibleMonth(addDays(startOfMonth(visibleMonth), -1))} className="rounded p-1 text-gray-500 hover:bg-gray-100">
                                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" /></svg>
                                </button>
                                <span className="text-sm font-semibold text-gray-800">{MONTH_LABELS[visibleMonth.getMonth()]} {visibleMonth.getFullYear()}</span>
                                <button type="button" onClick={() => setVisibleMonth(addDays(endOfMonth(visibleMonth), 1))} className="rounded p-1 text-gray-500 hover:bg-gray-100">
                                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" /></svg>
                                </button>
                            </div>
                            <div className="grid grid-cols-7 gap-0.5 text-center text-[11px] text-gray-400">
                                {DAY_LABELS.map((d) => <div key={d} className="py-1">{d}</div>)}
                            </div>
                            <div className="grid grid-cols-7 gap-0.5">
                                {monthDays.map((day) => {
                                    const dayIso = toISO(day);
                                    const isCurrentMonth = day.getMonth() === visibleMonth.getMonth();
                                    const isToday = dayIso === toISO(today);
                                    return (
                                        <button
                                            type="button"
                                            key={dayIso}
                                            onClick={() => handleDayClick(dayIso)}
                                            className={`h-8 rounded-md text-xs transition-colors
                                                ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-700'}
                                                ${inRange(dayIso) ? 'bg-emerald-100' : ''}
                                                ${isEdge(dayIso) ? 'bg-emerald-600 !text-white font-semibold' : ''}
                                                ${isToday && !isEdge(dayIso) ? 'ring-1 ring-emerald-400' : ''}
                                                hover:bg-emerald-50`}
                                        >
                                            {day.getDate()}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Raccourcis */}
                        <div className="w-40 flex-shrink-0 border-l border-gray-100 pl-4">
                            <ul className="space-y-1 text-sm text-gray-600">
                                {PRESETS.map((preset) => (
                                    <li key={preset.key}>
                                        <button
                                            type="button"
                                            onClick={() => applyPreset(preset)}
                                            className="w-full rounded-md px-2 py-1.5 text-left hover:bg-gray-100"
                                        >
                                            {preset.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Dates personnalisées */}
                    <div className="mt-4 grid grid-cols-2 gap-3 border-t border-gray-100 pt-3">
                        <label className="text-xs font-medium text-gray-500">
                            Date de début
                            <input
                                type="date"
                                value={pendingFrom || ''}
                                onChange={(e) => setPendingFrom(e.target.value)}
                                className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm text-gray-700"
                            />
                        </label>
                        <label className="text-xs font-medium text-gray-500">
                            Date de fin
                            <input
                                type="date"
                                value={pendingTo || ''}
                                onChange={(e) => setPendingTo(e.target.value)}
                                className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm text-gray-700"
                            />
                        </label>
                    </div>

                    <div className="mt-4 flex justify-end gap-2 border-t border-gray-100 pt-3">
                        <button type="button" onClick={cancel} className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-100">
                            Annuler
                        </button>
                        <button type="button" onClick={confirm} className="rounded-lg bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700">
                            Terminé
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────────────
// Zone défilable réutilisable : ajoute des indicateurs (dégradé + pastille)
// en haut et/ou en bas dès qu'il reste du contenu caché, et s'adapte à la
// hauteur disponible (responsive) via des classes de hauteur maximale.
// ────────────────────────────────────────────────────────────────────────────
function ScrollableArea({ children, maxHeightClass = 'max-h-[45vh] sm:max-h-[55vh] lg:max-h-[60vh]', className = '' }) {
    const scrollRef = useRef(null);
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);

    const updateScrollState = () => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollUp(el.scrollTop > 4);
        setCanScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
    };

    useEffect(() => {
        updateScrollState();
        const el = scrollRef.current;
        if (!el) return undefined;
        const resizeObserver = new ResizeObserver(updateScrollState);
        resizeObserver.observe(el);
        window.addEventListener('resize', updateScrollState);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateScrollState);
        };
    }, [children]);

    return (
        <div className="relative min-h-0 flex-1">
            {/* Indicateur : d'autres lignes au-dessus */}
            {canScrollUp && (
                <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center">
                    <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white via-white/80 to-transparent" />
                    <span className="relative mt-1 flex items-center gap-1 rounded-full bg-gray-800/90 px-2.5 py-0.5 text-[10px] font-medium text-white shadow-sm">
                        ▲ lignes précédentes
                    </span>
                </div>
            )}

            <div
                ref={scrollRef}
                onScroll={updateScrollState}
                className={`overflow-y-auto ${maxHeightClass} ${className}`}
            >
                {children}
            </div>

            {/* Indicateur : d'autres lignes en dessous */}
            {canScrollDown && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center">
                    <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white via-white/80 to-transparent" />
                    <span className="relative mb-1 flex items-center gap-1 rounded-full bg-gray-800/90 px-2.5 py-0.5 text-[10px] font-medium text-white shadow-sm">
                        ▼ autres lignes
                    </span>
                </div>
            )}
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────────────
// Modale plein écran réutilisable : overlay sombre + panneau centré, fermeture
// via le bouton, un clic à l'extérieur, ou la touche Échap.
// ────────────────────────────────────────────────────────────────────────────
function FullscreenModal({ title, subtitle, onClose, children }) {
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = '';
        };
    }, [onClose]);

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-3 sm:p-6"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 flex-shrink-0">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
                        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                        aria-label="Fermer"
                    >
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-5">
                    {children}
                </div>
            </div>
        </div>
    );
}

// Petit bouton icône "agrandir" utilisé sur les cartes graphique / listes.
function ExpandButton({ onClick, label = 'Plein écran' }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={label}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-emerald-700 hover:border-emerald-200 transition-colors"
        >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V5a1 1 0 011-1h3M20 8V5a1 1 0 00-1-1h-3M4 16v3a1 1 0 001 1h3m12-4v3a1 1 0 01-1 1h-3" />
            </svg>
            <span className="hidden sm:inline">{label}</span>
        </button>
    );
}

// Colonnes du tableau "Ventes par produit" — largeurs partagées entre
// l'en-tête, le corps (scrollable) et le pied (total fixe) pour qu'ils
// restent alignés.
const PRODUCT_COLUMNS = [
    { key: 'product', label: 'Produit', width: '24%', align: 'left' },
    { key: 'qty', label: 'Qté', width: '9%', align: 'right' },
    { key: 'margin', label: 'Marge', width: '13%', align: 'right' },
    { key: 'rate', label: 'Taux', width: '9%', align: 'right' },
    { key: 'expected', label: 'CA attendu', width: '15%', align: 'right' },
    { key: 'reported', label: 'Montant saisi', width: '15%', align: 'right' },
    { key: 'diff', label: 'Différence', width: '15%', align: 'right' },
];

function ProductColgroup() {
    return (
        <colgroup>
            {PRODUCT_COLUMNS.map((col) => (
                <col key={col.key} style={{ width: col.width }} />
            ))}
        </colgroup>
    );
}

// ────────────────────────────────────────────────────────────────────────────
// Petit composant pour une ligne du récapitulatif (label + valeur), utilisé
// à l'intérieur des cartes groupées du récap.
// ────────────────────────────────────────────────────────────────────────────
function RecapRow({ label, value, tone = 'default', strong = false }) {
    const toneClasses = {
        default: 'text-gray-900',
        green: 'text-green-700',
        amber: 'text-amber-700',
    };
    return (
        <div className="flex items-center justify-between py-1">
            <span className="text-xs text-gray-500">{label}</span>
            <span className={`text-sm ${strong ? 'font-semibold' : 'font-medium'} ${toneClasses[tone]}`}>{value}</span>
        </div>
    );
}

function RecapCard({ title, children, loading }) {
    return (
        <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-3">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{title}</p>
            {loading ? <p className="py-2 text-sm text-gray-400">…</p> : <div className="divide-y divide-gray-100">{children}</div>}
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────────────
// Vue combinée : Rapport des ventes + Paiements journaliers, dans une seule
// vue compacte pilotée par un seul filtre de plage de dates.
// ────────────────────────────────────────────────────────────────────────────
const initialForm = {
    montant: '',
    mode_payement_id: '',
    esp_recus: '',
};

function RevenueChart({ data, loading }) {
    const [activePoint, setActivePoint] = useState(null);

    const chartData = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) return [];
        const safeData = data
            .filter((item) => item && Number.isFinite(Number(item.total)))
            .map((item) => ({ ...item, total: Number(item.total || 0) }))
            .filter((item) => item.total > 0);

        if (safeData.length <= 160) return safeData;
        const stride = Math.ceil(safeData.length / 160);
        return safeData.filter((_, index) => index % stride === 0 || index === safeData.length - 1);
    }, [data]);

    const maxValue = useMemo(() => Math.max(...chartData.map((item) => item.total), 1), [chartData]);

    const width = 980;
    const height = 320;
    const padding = { top: 18, right: 16, bottom: 32, left: 42 };
    const innerWidth = width - padding.left - padding.right;
    const innerHeight = height - padding.top - padding.bottom;

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center rounded-2xl border border-gray-100 bg-gray-50/70 text-sm text-gray-400">
                Chargement du graphique…
            </div>
        );
    }

    if (chartData.length === 0) {
        return (
            <div className="flex h-64 items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 text-sm text-gray-400">
                Aucune donnée de chiffre d'affaires disponible.
            </div>
        );
    }

    const points = chartData.map((item, index) => {
        const x = padding.left + (index / Math.max(chartData.length - 1, 1)) * innerWidth;
        const y = padding.top + innerHeight - (item.total / maxValue) * innerHeight;
        return { ...item, x, y };
    });

    const linePath = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`).join(' ');
    const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(2)} ${padding.top + innerHeight} L ${points[0].x.toFixed(2)} ${padding.top + innerHeight} Z`;

    const gridLevels = [0.25, 0.5, 0.75, 1];

    return (
        <div className="rounded-2xl border border-gray-100 bg-gradient-to-br from-emerald-50/70 to-white p-3">
            <svg viewBox={`0 0 ${width} ${height}`} className="h-[320px] w-full">
                {gridLevels.map((level) => {
                    const y = padding.top + innerHeight - level * innerHeight;
                    return (
                        <g key={level}>
                            <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#e5e7eb" strokeDasharray="4 4" />
                            <text x={padding.left - 10} y={y + 4} textAnchor="end" fontSize="11" fill="#6b7280">
                                {formatAr(maxValue * level)}
                            </text>
                        </g>
                    );
                })}

                <path d={areaPath} fill="rgba(16, 185, 129, 0.16)" />
                <path d={linePath} fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" />
                {points.map((point) => (
                    <g key={`${point.date}-${point.x}`}>
                        <circle
                            cx={point.x}
                            cy={point.y}
                            r="3"
                            fill="#10b981"
                            stroke="#ffffff"
                            strokeWidth="1.5"
                            className="cursor-pointer"
                            onClick={() => setActivePoint(point)}
                        />
                    </g>
                ))}

                {activePoint && (
                    <g>
                        <rect x={activePoint.x - 62} y={activePoint.y - 42} width="124" height="30" rx="8" fill="#111827" />
                        <text x={activePoint.x} y={activePoint.y - 23} textAnchor="middle" fontSize="11" fill="#ffffff">
                            {activePoint.date} • {formatAr(activePoint.total)}
                        </text>
                    </g>
                )}

                <text x={padding.left} y={height - 8} fontSize="11" fill="#6b7280">
                    {points[0]?.date || ''}
                </text>
                <text x={width - padding.right} y={height - 8} textAnchor="end" fontSize="11" fill="#6b7280">
                    {points[points.length - 1]?.date || ''}
                </text>
            </svg>
        </div>
    );
}

export default function SalesOverviewTab({ user }) {
    // Filtre de plage de dates partagé
    const [dateFrom, setDateFrom] = useState(toISO(addDays(today, -6)));
    const [dateTo, setDateTo] = useState(toISO(today));

    // Ventes
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState(null);
    const [loadingSales, setLoadingSales] = useState(false);
    const [dailyRevenueSeries, setDailyRevenueSeries] = useState([]);
    const [loadingDailyChart, setLoadingDailyChart] = useState(false);
    const [file, setFile] = useState(null);
    const [importMessage, setImportMessage] = useState('');
    const [importMessageType, setImportMessageType] = useState('');
    const [skippedRows, setSkippedRows] = useState([]);
    const [showSkippedRows, setShowSkippedRows] = useState(false);

    // Paiements
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [entries, setEntries] = useState([]);
    const [form, setForm] = useState(initialForm);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [loadingPayments, setLoadingPayments] = useState(false);
    const [paymentMessage, setPaymentMessage] = useState('');
    const [aggregatedReconciliation, setAggregatedReconciliation] = useState(null);
    const [editingEntryId, setEditingEntryId] = useState(null);
    const [editingEntry, setEditingEntry] = useState({});

    const canEdit = ['admin', 'direction'].includes((user?.role?.name || '').toLowerCase());

    // ── Chargement du rapport de ventes pour la période sélectionnée ──
    const fetchSalesReport = async () => {
        setLoadingSales(true);
        try {
            const params = new URLSearchParams();
            if (dateFrom) params.append('date_from', dateFrom);
            if (dateTo) params.append('date_to', dateTo);
            const resp = await fetch('/api/sales/report?' + params.toString(), { credentials: 'same-origin' });
            const data = await resp.json();
            setRows(data.rows || []);
            setTotals(data.totals || null);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingSales(false);
        }
    };

    // ── Chargement du graphique du CA journalier depuis les données disponibles ──
    const fetchSalesSummary = async () => {
        setLoadingDailyChart(true);
        try {
            const params = new URLSearchParams();
            params.append('date_to', toISO(today));
            const resp = await fetch('/api/sales/report/summary?' + params.toString(), { credentials: 'same-origin' });
            const text = await resp.text();

            if (!resp.ok) {
                throw new Error(`Impossible de charger le graphique (${resp.status})`);
            }

            let data = {};
            try {
                data = text ? JSON.parse(text) : {};
            } catch (parseError) {
                console.error('Graph payload is not valid JSON', parseError, text);
                data = {};
            }

            setDailyRevenueSeries(data.dailyRevenue || []);
        } catch (error) {
            console.error(error);
            setDailyRevenueSeries([]);
        } finally {
            setLoadingDailyChart(false);
        }
    };

    // ── Chargement des modes de paiement + entrées de la période ──
    const fetchPaymentsData = async () => {
        setLoadingPayments(true);
        try {
            const [methodsResponse, entriesResponse] = await Promise.all([
                fetch('/api/daily-payments/methods', { credentials: 'same-origin' }),
                fetch('/api/daily-payments', { credentials: 'same-origin' }),
            ]);

            if (methodsResponse.ok) {
                setPaymentMethods(await methodsResponse.json());
            }
            if (entriesResponse.ok) {
                const data = await entriesResponse.json();
                setEntries(data.entries || []);
            }

            // Agrégation de la réconciliation jour par jour sur toute la période
            const dates = listDatesBetween(dateFrom, dateTo);
            const results = await Promise.all(
                dates.map((d) =>
                    fetch(`/api/daily-payments/reconciliation?date=${d}`, { credentials: 'same-origin' })
                        .then((r) => (r.ok ? r.json() : null))
                        .catch(() => null)
                )
            );

            const aggregate = {
                totalPayments: 0,
                totalEspRecus: 0,
                totalSales: 0,
                totalItemsSold: 0,
                discrepancy: 0,
                paymentTotals: {},
                soldItems: {},
            };

            results.filter(Boolean).forEach((day) => {
                aggregate.totalPayments += Number(day.totalPayments || 0);
                aggregate.totalEspRecus += Number(day.totalEspRecus || 0);
                aggregate.totalSales += Number(day.totalSales || 0);
                aggregate.totalItemsSold += Number(day.totalItemsSold || 0);
                aggregate.discrepancy += Number(day.discrepancy || 0);

                Object.entries(day.paymentTotals || {}).forEach(([key, value]) => {
                    if (!aggregate.paymentTotals[key]) {
                        aggregate.paymentTotals[key] = { label: value.label, total: 0 };
                    }
                    aggregate.paymentTotals[key].total += Number(value.total || 0);
                });

                (day.soldItems || []).forEach((item) => {
                    if (!aggregate.soldItems[item.name]) {
                        aggregate.soldItems[item.name] = { name: item.name, quantity: 0, total: 0 };
                    }
                    aggregate.soldItems[item.name].quantity += Number(item.quantity || 0);
                    aggregate.soldItems[item.name].total += Number(item.total || 0);
                });
            });

            setAggregatedReconciliation(aggregate);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingPayments(false);
        }
    };

    useEffect(() => {
        fetchSalesReport();
        fetchPaymentsData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateFrom, dateTo]);

    useEffect(() => {
        fetchSalesSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRangeChange = (from, to) => {
        setDateFrom(from);
        setDateTo(to);
    };

    const filteredEntries = useMemo(
        () => entries.filter((e) => {
            const d = (e.date || e.created_at || '').slice(0, 10);
            return d >= dateFrom && d <= dateTo;
        }),
        [entries, dateFrom, dateTo]
    );

    const paymentTotalsList = useMemo(() => {
        if (!aggregatedReconciliation?.paymentTotals) return [];
        return Object.entries(aggregatedReconciliation.paymentTotals).map(([key, value]) => ({
            key,
            label: value.label,
            total: value.total,
        }));
    }, [aggregatedReconciliation]);

    const soldItemsList = useMemo(() => {
        if (!aggregatedReconciliation?.soldItems) return [];
        return Object.values(aggregatedReconciliation.soldItems).sort((a, b) => b.total - a.total);
    }, [aggregatedReconciliation]);

    const rowTotal = (row) => Number(row.avg_price) * Number(row.quantity_sold);
    const marginRate = (row) => {
        const revenue = rowTotal(row);
        if (!revenue) return 0;
        return ((Number(row.total_margin) / revenue) * 100).toFixed(1);
    };
    const totalExpected = rows.reduce((s, r) => s + (Number(r.expected_revenue || 0)), 0);
    const totalDifference = rows.reduce((s, r) => s + (Number(r.difference || 0)), 0);
    const totalReported = rows.reduce((s, r) => s + (Number(r.reported_total || 0)), 0);
    const totalReportedMargin = rows.reduce((s, r) => s + (Number(r.reported_margin || 0)), 0);
    const totalRevenue = rows.reduce((s, r) => s + (Number(r.revenue || 0)), 0);
    const totalPaymentValue = Number(aggregatedReconciliation?.totalPayments || 0);
    const totalEspRecus = Number(aggregatedReconciliation?.totalEspRecus || 0);
    const totalPaymentDiff = totalPaymentValue - totalEspRecus;
    const totalPriceDiff = totalExpected - totalRevenue;
    const totalMarginDiff = Number(totals?.total_margin || 0) - totalReportedMargin;
    const totalPurchaseValue = Number(totals?.purchase_total || 0);

    // ── Import CSV des ventes ──
    const handleImport = async () => {
        if (!file) {
            setImportMessage('Sélectionnez un fichier CSV');
            setImportMessageType('error');
            return;
        }
        setLoadingSales(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const resp = await fetch('/api/import/sales', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '' },
                body: fd,
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.error || data.message || 'Import failed');

            const msg = `Import réussi : ${data.created} ligne(s) ajoutée(s)${data.skipped > 0 ? ` (${data.skipped} ignorée(s))` : ''}`;
            setImportMessage(msg);
            setImportMessageType('success');
            setSkippedRows(data.skipped_rows || []);
            setShowSkippedRows((prev) => (data.skipped_rows && data.skipped_rows.length > 0 ? true : prev));
            setFile(null);
            fetchSalesReport();
            fetchPaymentsData();
        } catch (err) {
            setImportMessage(err.message || "Erreur lors de l'import");
            setImportMessageType('error');
            setSkippedRows([]);
        } finally {
            setLoadingSales(false);
        }
    };

    // ── Paiements : ajout / édition ──
    const handleSubmitPayment = async (event) => {
        event.preventDefault();
        setPaymentMessage('');
        try {
            const response = await fetch('/api/daily-payments', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                },
                body: JSON.stringify({ ...form, date: dateTo }),
            });
            const payload = await response.json();
            if (!response.ok) throw new Error(payload.message || 'Erreur lors de l’enregistrement');
            setPaymentMessage(payload.message || 'Paiement enregistré');
            setForm(initialForm);
            await fetchPaymentsData();
        } catch (error) {
            setPaymentMessage(error.message);
        }
    };

    const startEditing = (entry) => {
        setEditingEntryId(entry.id);
        setEditingEntry({
            date: entry.date,
            montant: entry.montant.toString(),
            mode_payement_id: entry.payment_method?.id || '',
            esp_recus: entry.esp_recus?.toString() || '0',
        });
    };

    const cancelEditing = () => {
        setEditingEntryId(null);
        setEditingEntry({});
    };

    const saveEditing = async (event) => {
        event.preventDefault();
        if (!editingEntryId) return;
        try {
            const response = await fetch(`/api/daily-payments/${editingEntryId}`, {
                method: 'PUT',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                },
                body: JSON.stringify(editingEntry),
            });
            const payload = await response.json();
            if (!response.ok) throw new Error(payload.message || 'Erreur lors de la mise à jour');
            setEntries((current) => current.map((entry) => (entry.id === payload.entry.id ? payload.entry : entry)));
            setPaymentMessage(payload.message || 'Paiement mis à jour');
            cancelEditing();
            await fetchPaymentsData();
        } catch (error) {
            setPaymentMessage(error.message);
        }
    };

    const loading = loadingSales || loadingPayments;

    return (
        <div className="space-y-4 p-1">
            {/* ── En-tête + filtre de période partagé ── */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900">Ventes & Paiements</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Vue combinée du chiffre d'affaires et des encaissements</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={handleRangeChange} />
                    <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        {file ? file.name : 'CSV'}
                        <input type="file" accept=".csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                    </label>
                    <button
                        onClick={handleImport}
                        disabled={loadingSales || !file}
                        className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                        Importer
                    </button>
                </div>
            </div>

            {importMessage && (
                <div className={`rounded-lg px-4 py-3 text-sm flex flex-col gap-1 ${importMessageType === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                    <div>{importMessage}</div>
                    {skippedRows.length > 0 && (
                        <div className="text-xs text-gray-700 mt-1">
                            <button type="button" onClick={() => setShowSkippedRows((s) => !s)} className="underline">{showSkippedRows ? 'Masquer' : 'Afficher'} la/les ligne(s) ignorée(s)</button>
                        </div>
                    )}
                    {importMessageType === 'success' && (
                        <div className="text-xs opacity-75 mt-1">
                            Format CSV : product, quantité vendue, prix de vente, [réduction %], [raison perte], [qté perdue], [raison annulation], [date]
                        </div>
                    )}
                </div>
            )}

            {showSkippedRows && skippedRows.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 p-3 text-sm">
                    <div className="font-semibold mb-2">Lignes ignorées</div>
                    <ul className="space-y-2 max-h-48 overflow-auto">
                        {skippedRows.map((r, idx) => (
                            <li key={idx} className="text-xs">
                                <div className="font-medium">Ligne {r.line} — Raison : {r.reason}</div>
                                <div className="text-gray-700 truncate">{(r.raw || []).join(' | ')}</div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* ── Récapitulatif ventes & paiements, regroupé par thème pour être plus lisible ── */}
            <div className="sticky top-0 z-20 rounded-3xl border border-gray-200 bg-white/95 p-3 backdrop-blur-sm shadow-sm">
                <div className="grid gap-3 sm:grid-cols-4">
                    <RecapCard title="Paiements" loading={loading}>
                        <RecapRow label="Saisis" value={formatAr(totalPaymentValue)} />
                        <RecapRow label="Reçus" value={formatAr(totalEspRecus)} />
                        <RecapRow
                            label="Écart"
                            value={formatAr(totalPaymentDiff)}
                            tone={totalPaymentDiff === 0 ? 'green' : 'amber'}
                            strong
                        />
                    </RecapCard>

                    <RecapCard title="Achats" loading={loading}>
                        <RecapRow
                            label="Total achats ingredients"
                            value={formatAr(totalPurchaseValue)}
                            tone="amber"
                            strong
                        />
                    </RecapCard>

                    <RecapCard title="Prix de vente" loading={loading}>
                        <RecapRow label="CA attendu" value={formatAr(totalExpected)} />
                        <RecapRow label="CA réel" value={formatAr(totalReported)} />
                        <RecapRow
                            label="Écart entre les deux"
                            value={formatAr(totalDifference)}
                            tone={totalDifference === 0 ? 'green' : 'amber'}
                            strong
                        />
                    </RecapCard>

                    <RecapCard title="Chiffre d'affaires & marge" loading={loading}>
                        <RecapRow label="CA attendu" value={formatAr(totalExpected)} />
                        <RecapRow label="CA réel" value={formatAr(totalRevenue)} />
                        <RecapRow label="Marge réelle" value={formatAr(totals?.total_margin || 0)} tone="green" />
                        <RecapRow
                            label="Écart marge"
                            value={formatAr(totalMarginDiff)}
                            tone={totalMarginDiff === 0 ? 'green' : 'amber'}
                            strong
                        />
                    </RecapCard>
                </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-800">Évolution du chiffre d'affaires</h3>
                        <p className="text-xs text-gray-400">CA journalier depuis les données disponibles</p>
                    </div>
                </div>
                <RevenueChart data={dailyRevenueSeries} loading={loadingDailyChart} />
            </div>

            {/* ── Deux colonnes : ventes par produit / paiements ── */}
            <div className="grid gap-4 xl:grid-cols-2">
                {/* Ventes par produit */}
                <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-800">Ventes par produit</h3>
                        <span className="text-xs text-gray-400">{rows.length} produit(s)</span>
                    </div>

                    {/* En-tête fixe */}
                    <table className="w-full table-fixed text-sm">
                        <ProductColgroup />
                        <thead className="bg-gray-50">
                            <tr className="border-b border-gray-100">
                                {PRODUCT_COLUMNS.map((col) => (
                                    <th
                                        key={col.key}
                                        className={`px-3 py-2 text-${col.align} text-xs font-medium uppercase tracking-wide text-gray-400`}
                                    >
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                    </table>

                    {/* Corps défilable, avec indicateurs de lignes cachées, hauteur adaptée à l'écran */}
                    <ScrollableArea>
                        <table className="w-full table-fixed text-sm">
                            <ProductColgroup />
                            <tbody className="divide-y divide-gray-50">
                                {rows.length === 0 ? (
                                    <tr><td colSpan={PRODUCT_COLUMNS.length} className="px-3 py-8 text-center text-sm text-gray-400">Aucune donnée pour cette période.</td></tr>
                                ) : rows.map((r) => (
                                    <tr key={r.dish_id} className="hover:bg-gray-50/60 transition-colors">
                                        <td className="px-3 py-2 font-medium text-gray-900">{r.product}</td>
                                        <td className="px-3 py-2 text-right text-gray-700">{Number(r.quantity_sold).toLocaleString()}</td>
                                        <td className="px-3 py-2 text-right text-green-700 font-medium">{formatAr(r.total_margin)}</td>
                                        <td className="px-3 py-2 text-right">
                                            <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">{marginRate(r)} %</span>
                                        </td>
                                        <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">{formatAr(r.expected_revenue)}</td>
                                        <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">{formatAr(r.reported_total)}</td>
                                        <td className="px-3 py-2 text-right font-semibold text-gray-900">{formatAr(r.difference)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </ScrollableArea>

                    {/* Pied de tableau : total toujours visible, fixé en bas de la carte */}
                    {totals && (
                        <table className="w-full table-fixed text-sm">
                            <ProductColgroup />
                            <tfoot>
                                <tr className="border-t-2 border-gray-200 bg-gray-50/90">
                                    <td className="px-3 py-2 font-semibold text-gray-700">Totaux</td>
                                    <td className="px-3 py-2 text-right font-semibold text-gray-700">{Number(totals.total_quantity).toLocaleString()}</td>
                                    <td className="px-3 py-2 text-right font-semibold text-green-700">{formatAr(totals.total_margin)}</td>
                                    <td />
                                    <td className="px-3 py-2 text-right font-semibold text-gray-900">{formatAr(totalExpected)}</td>
                                    <td className="px-3 py-2 text-right font-semibold text-gray-900">{formatAr(totalReported)}</td>
                                    <td className="px-3 py-2 text-right font-semibold text-gray-900">{formatAr(totalDifference)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </div>

                {/* Paiements journaliers */}
                <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-800">Paiements journaliers</h3>
                        <button
                            type="button"
                            onClick={() => setShowPaymentForm((prev) => !prev)}
                            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                        >
                            {showPaymentForm ? 'Fermer' : '+ Ajouter'}
                        </button>
                    </div>

                    {showPaymentForm && (
                        <form onSubmit={handleSubmitPayment} className="grid grid-cols-3 gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/60">
                            <label className="text-xs text-gray-500">
                                Montant
                                <input
                                    type="number" step="0.01" required
                                    value={form.montant}
                                    onChange={(e) => setForm({ ...form, montant: e.target.value })}
                                    className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-black placeholder:text-slate-400"
                                />
                            </label>
                            <label className="text-xs text-gray-500">
                                Mode de paiement
                                <select
                                    required value={form.mode_payement_id}
                                    onChange={(e) => setForm({ ...form, mode_payement_id: e.target.value })}
                                    className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-black"
                                >
                                    <option value="">Mode</option>
                                    {paymentMethods.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                                </select>
                            </label>
                            <label className="text-xs text-gray-500">
                                Espèces reçues
                                <input
                                    type="number" step="0.01"
                                    value={form.esp_recus}
                                    onChange={(e) => setForm({ ...form, esp_recus: e.target.value })}
                                    className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-black placeholder:text-slate-400"
                                />
                            </label>
                            <button type="submit" className="col-span-3 rounded-lg bg-slate-800 px-3 py-1.5 text-sm font-semibold text-white">
                                Enregistrer (date : {formatDisplay(dateTo)})
                            </button>
                            {paymentMessage && <p className="col-span-3 text-xs text-emerald-700">{paymentMessage}</p>}
                        </form>
                    )}

                    <div className="grid grid-cols-2 gap-3 px-4 py-3 border-b border-gray-100">
                        <div>
                            <p className="text-sm text-gray-400 mb-1">Par mode de paiement</p>
                            <div className="space-y-1">
                                {paymentTotalsList.length === 0 ? (
                                    <p className="text-sm text-gray-400">Aucun total.</p>
                                ) : paymentTotalsList.map((item) => (
                                    <div key={item.key} className="flex items-center justify-between text-sm">
                                        <span className="text-gray-600">{item.label}</span>
                                        <span className="font-semibold text-gray-900">{formatAr(item.total)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <p className="text-sm text-gray-400 mb-1">Top articles vendus</p>
                            <div className="space-y-1">
                                {soldItemsList.length === 0 ? (
                                    <p className="text-sm text-gray-400">Aucune donnée.</p>
                                ) : soldItemsList.slice(0, 4).map((item) => (
                                    <div key={item.name} className="flex items-center justify-between text-sm">
                                        <span className="text-gray-600 truncate">{item.name}</span>
                                        <span className="font-semibold text-gray-900">{item.quantity.toFixed(0)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <ScrollableArea maxHeightClass="max-h-[45vh] sm:max-h-[55vh] lg:max-h-[60vh]">
                        <div className="divide-y divide-gray-50 text-sm">
                            {filteredEntries.length === 0 ? (
                                <p className="px-4 py-6 text-center text-sm text-gray-400">Aucun paiement pour cette période.</p>
                            ) : filteredEntries.map((entry) => {
                                const isEditing = editingEntryId === entry.id;
                                return (
                                    <div key={entry.id} className="px-4 py-2.5">
                                        {isEditing ? (
                                            <form onSubmit={saveEditing} className="grid grid-cols-2 gap-2">
                                                <label className="text-xs text-gray-500">
                                                    Date
                                                    <input
                                                        type="date" required value={editingEntry.date}
                                                        onChange={(e) => setEditingEntry({ ...editingEntry, date: e.target.value })}
                                                        className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-black"
                                                    />
                                                </label>
                                                <label className="text-xs text-gray-500">
                                                    Montant
                                                    <input
                                                        type="number" step="0.01" required value={editingEntry.montant}
                                                        onChange={(e) => setEditingEntry({ ...editingEntry, montant: e.target.value })}
                                                        className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-black"
                                                    />
                                                </label>
                                                <label className="text-xs text-gray-500">
                                                    Espèces reçues
                                                    <input
                                                        type="number" step="0.01"
                                                        value={editingEntry.esp_recus}
                                                        onChange={(e) => setEditingEntry({ ...editingEntry, esp_recus: e.target.value })}
                                                        className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-black placeholder:text-slate-400"
                                                    />
                                                </label>
                                                <label className="col-span-2 text-xs text-gray-500">
                                                    Mode de paiement
                                                    <select
                                                        required value={editingEntry.mode_payement_id}
                                                        onChange={(e) => setEditingEntry({ ...editingEntry, mode_payement_id: e.target.value })}
                                                        className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-black"
                                                    >
                                                        <option value="">Mode</option>
                                                        {paymentMethods.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                                                    </select>
                                                </label>
                                                <div className="col-span-2 flex gap-2">
                                                    <button type="submit" className="rounded-lg bg-emerald-700 px-3 py-1 text-xs font-semibold text-white">Sauvegarder</button>
                                                    <button type="button" onClick={cancelEditing} className="rounded-lg border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700">Annuler</button>
                                                </div>
                                            </form>
                                        ) : (
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">{entry.payment_method?.name || 'Mode inconnu'}</p>
                                                    <p className="text-xs text-gray-400">{entry.date}</p>
                                                    <p className="text-xs text-gray-500 mt-1">Montant saisi : {formatAr(entry.montant)}</p>
                                                    {entry.esp_recus != null && entry.esp_recus !== '' && (
                                                        <p className="text-xs text-gray-500">Espèces reçues : {formatAr(entry.esp_recus)}</p>
                                                    )}
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <p className="text-sm font-semibold text-gray-900">{formatAr(entry.montant)}</p>
                                                </div>
                                                {canEdit && (
                                                    <button type="button" onClick={() => startEditing(entry)} className="flex-shrink-0 rounded-lg bg-slate-800 px-2 py-1 text-xs font-semibold text-white">
                                                        Modifier
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </ScrollableArea>
                </div>
            </div>
        </div>
    );
}